import { CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api'
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/Auth'

interface FormData {
  packageName: string
  shortDescription: string
  whatsIncluded: string[]
  whatsNotIncluded: string[]
  importantInfo: string[]
  meetingPoints: Array<{
    title: string
    subtitle: string
    lat: number | null
    long: number | null
    details: string
  }>
  accordionOpen: boolean
  scheduleAccordionOpen: boolean
  itineraries: Array<{
    title: string
    duration: string
    details: string
    hasPhoto: boolean
    images: string[]
  }>
  schedule: string
  scheduleHasPhoto: boolean
  scheduleImages: string[]
  pricePerPerson: string
  pricePerChild: string
  freeForChild: boolean
  extraExpenses: string
  discount: string
  currency: string
  discountType: string
  [key: string]: any
}

interface StepProps {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: FormData
  updateFormData: (data: Partial<FormData>) => void
}

const libraries = ['places', 'geometry'] as any[]

const Step4: React.FC<StepProps> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [currentUploadingItinerary, setCurrentUploadingItinerary] = useState<number | null>(null)
  const [scheduleUploading, setScheduleUploading] = useState(false)

  // Remove AI generation states as they're not needed anymore

  const itineraryFileInputRefs = useRef<(HTMLInputElement | null)[]>([])
  const scheduleFileInputRef = useRef<HTMLInputElement>(null)
  const { userLoggedIn } = useAuth()

  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const mapRefs = useRef<{ [key: number]: google.maps.Map | null }>({})

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries,
  })

  // Initialize Geocoder after the API is loaded
  useEffect(() => {
    if (isLoaded) {
      geocoderRef.current = new window.google.maps.Geocoder()
    }
  }, [isLoaded])

  // Function to perform reverse geocoding
  const reverseGeocode = (lat: number, lng: number, index: number) => {
    if (!geocoderRef.current || !isLoaded) return

    // Try Places Nearby Search first for building names
    if (window.google?.maps?.places?.PlacesService) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'))

      const request = {
        location: new window.google.maps.LatLng(lat, lng),
        radius: 50, // Smaller radius for more precise results
      }

      service.nearbySearch(request, (places, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          places &&
          places.length > 0
        ) {
          // Find the closest place
          let closestPlace = places[0]
          let minDistance = Number.MAX_VALUE

          for (const place of places) {
            if (place.geometry?.location) {
              const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
                new window.google.maps.LatLng(lat, lng),
                place.geometry.location,
              )
              if (distance < minDistance) {
                minDistance = distance
                closestPlace = place
              }
            }
          }

          // Use the closest place if it's within 100 meters
          if (minDistance <= 100 && closestPlace.name) {
            handleMeetingPointChange(index, {
              title: closestPlace.name,
              subtitle: closestPlace.vicinity || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              lat,
              long: lng,
            })
            return
          }
        }

        // Fallback to geocoding
        performGeocodeFallback(lat, lng, index)
      })
    } else {
      // Direct fallback if Places API not available
      performGeocodeFallback(lat, lng, index)
    }
  }

  const performGeocodeFallback = (lat: number, lng: number, index: number) => {
    const location = { lat, lng }
    geocoderRef.current?.geocode({ location }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        let bestResult = results[0]
        let bestTitle = 'Selected Location'

        // Look through all results for the best match
        for (const result of results) {
          // Check if this result represents a named place
          const isPointOfInterest = result.types.includes('point_of_interest')
          const isEstablishment = result.types.includes('establishment')
          const isPremise = result.types.includes('premise')

          if (isPointOfInterest || isEstablishment || isPremise) {
            bestResult = result

            // Try to get name from address components
            const nameComponent = result.address_components?.find(
              (c) =>
                c.types.includes('establishment') ||
                c.types.includes('point_of_interest') ||
                c.types.includes('premise'),
            )

            if (nameComponent) {
              bestTitle = nameComponent.long_name
              break
            }
          }
        }

        // If still no good name, extract from formatted address
        if (bestTitle === 'Selected Location' && bestResult.formatted_address) {
          const addressParts = bestResult.formatted_address.split(',')
          if (addressParts[0]) {
            const firstPart = addressParts[0].trim()
            // Don't use if it starts with numbers or plus codes
            if (!firstPart.match(/^[\d+]/)) {
              bestTitle = firstPart
            }
          }
        }

        handleMeetingPointChange(index, {
          title: bestTitle,
          subtitle: bestResult.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat,
          long: lng,
        })
      } else {
        handleMeetingPointChange(index, {
          title: 'Selected Location',
          subtitle: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat,
          long: lng,
        })
      }
    })
  }

  const getDraftId = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('draft_id')
  }

  const formatSingleContent = (text: string): string => {
    if (!text || typeof text !== 'string') return ''

    let cleaned = text.trim()

    cleaned = cleaned.replace(/```[\w]*\n?/g, '')
    cleaned = cleaned.replace(/^["']|["']$/g, '')
    cleaned = cleaned.replace(/,\s*$/, '')
    cleaned = cleaned.replace(/^\s*\[\s*/, '')
    cleaned = cleaned.replace(/\s*\]\s*$/, '')
    cleaned = cleaned.replace(/\s+/g, ' ')
    cleaned = cleaned.replace(/^\{|\}$/g, '')

    return cleaned.trim()
  }

  const formatGeneratedContent = (content: any): string | string[] => {
    if (!content) return ''

    if (Array.isArray(content)) {
      return content
        .filter((item) => {
          if (typeof item !== 'string') return false
          const cleaned = item.trim()
          return (
            cleaned !== '' &&
            cleaned !== '```json' &&
            cleaned !== '```' &&
            cleaned !== '[' &&
            cleaned !== ']' &&
            cleaned !== ',' &&
            !cleaned.match(/^```\w*$/)
          )
        })
        .map((item) => formatSingleContent(item))
        .filter((item) => item.trim() !== '')
    }

    return formatSingleContent(content)
  }

  // Remove individual content generation as it's now handled in Step 3

  // Remove individual generation handlers as AI content is generated in Step 3

  const styles = {
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #D6DAE0',
      borderRadius: '8px',
      fontSize: '16px',
      boxSizing: 'border-box' as const,
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    textarea: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #D6DAE0',
      borderRadius: '8px',
      fontSize: '16px',
      boxSizing: 'border-box' as const,
      fontFamily: 'Poppins, sans-serif',
      resize: 'vertical' as const,
      minHeight: '96px',
    } as CSSProperties,
    label: {
      display: 'block',
      fontSize: '16px',
      color: '#212427',
      marginBottom: '8px',
      fontWeight: '500',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    subLabel: {
      display: 'block',
      fontSize: '16px',
      color: '#212427',
      marginBottom: '8px',
      fontWeight: '500',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    section: {
      marginBottom: '24px',
    } as CSSProperties,
    subSection: {
      marginBottom: '16px',
    } as CSSProperties,
    actionButton: {
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      border: '1px solid',
    } as CSSProperties,
    charCount: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '4px',
      fontSize: '12px',
      color: '#667085',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    inputWithDropdown: {
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #D6DAE0',
      borderRadius: '8px',
      overflow: 'hidden',
    } as CSSProperties,
    flexInput: {
      flex: 1,
      border: 'none',
      outline: 'none',
      padding: '12px 16px',
      fontSize: '16px',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    dropdown: {
      padding: '12px 16px',
      borderLeft: '1px solid #D6DAE0',
      backgroundColor: '#F9FAFB',
      cursor: 'pointer',
      fontSize: '16px',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
    } as CSSProperties,
    checkbox: {
      width: '20px',
      height: '20px',
    } as CSSProperties,
  }

  const calculateProgress = React.useCallback(() => {
    let completedFields = 0
    const totalRequiredFields = 5

    if (formData.packageName?.trim()) completedFields++
    if (formData.whatsIncluded?.some((item) => item.trim())) completedFields++
    if (formData.whatsNotIncluded?.some((item) => item.trim())) completedFields++
    if (formData.importantInfo?.some((item) => item.trim())) completedFields++

    const isPricingComplete =
      formData.pricePerPerson?.trim() !== '' &&
      (formData.freeForChild || formData.pricePerChild?.trim() !== '')

    if (isPricingComplete) completedFields++

    return Math.min((completedFields / totalRequiredFields) * 100, 100)
  }, [
    formData.packageName,
    formData.whatsIncluded,
    formData.whatsNotIncluded,
    formData.importantInfo,
    formData.pricePerPerson,
    formData.pricePerChild,
    formData.freeForChild,
  ])

  useEffect(() => {
    const progress = calculateProgress()
    onStepProgress(progress)
  }, [
    formData.packageName,
    formData.whatsIncluded,
    formData.whatsNotIncluded,
    formData.importantInfo,
    formData.pricePerPerson,
    formData.pricePerChild,
    formData.freeForChild,
    calculateProgress,
  ])

  const handleInputChange = (field: string, value: string) => {
    updateFormData({ [field]: value })
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFormData({
      freeForChild: e.target.checked,
      ...(e.target.checked && { pricePerChild: '' }),
    })
  }

  const handleListItemChange = (listName: string, index: number, value: string) => {
    if (value.length > 200) return

    const currentArray = (Array.isArray(formData[listName]) ? formData[listName] : ['']) as string[]
    const newArray = currentArray.map((item: string, i: number) => (i === index ? value : item))

    updateFormData({ [listName]: newArray })
  }

  const addListItem = (listName: string) => {
    const currentArray = Array.isArray(formData[listName]) ? formData[listName] : ['']
    updateFormData({ [listName]: [...currentArray, ''] })
  }

  const removeListItem = (listName: string, index: number) => {
    const currentArray = Array.isArray(formData[listName]) ? formData[listName] : ['']
    if (currentArray.length > 1) {
      const newArray = currentArray.filter((_: string, i: number) => i !== index)
      updateFormData({ [listName]: newArray })
    }
  }

  const handleMeetingPointChange = (
    index: number,
    value: {
      title?: string
      subtitle?: string
      lat?: number | null
      long?: number | null
      details?: string
    },
  ) => {
    const currentMeetingPoints = formData.meetingPoints || [
      { title: '', subtitle: '', lat: null, long: null, details: '' },
    ]
    const newMeetingPoints = currentMeetingPoints.map((point, i) =>
      i === index ? { ...point, ...value } : point,
    )
    updateFormData({ meetingPoints: newMeetingPoints })
  }

  const addMeetingPoint = () => {
    const currentMeetingPoints = formData.meetingPoints || []
    updateFormData({
      meetingPoints: [
        ...currentMeetingPoints,
        { title: '', subtitle: '', lat: null, long: null, details: '' },
      ],
    })
  }

  const removeMeetingPoint = (index: number) => {
    const currentMeetingPoints = formData.meetingPoints || []
    if (currentMeetingPoints.length > 1) {
      const newMeetingPoints = currentMeetingPoints.filter((_, i) => i !== index)
      updateFormData({ meetingPoints: newMeetingPoints })
    }
  }

  const handleItineraryChange = (index: number, field: string, value: string) => {
    const currentItineraries = formData.itineraries || [
      { title: '', duration: '', details: '', hasPhoto: false, images: [] },
    ]
    const newItineraries = currentItineraries.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    )
    updateFormData({ itineraries: newItineraries })
  }

  const addItinerary = () => {
    const currentItineraries = formData.itineraries || []
    updateFormData({
      itineraries: [
        ...currentItineraries,
        { title: '', duration: '', details: '', hasPhoto: false, images: [] },
      ],
    })
  }

  const removeItinerary = (index: number) => {
    const currentItineraries = formData.itineraries || []
    if (currentItineraries.length > 1) {
      const newItineraries = currentItineraries.filter((_, i) => i !== index)
      updateFormData({ itineraries: newItineraries })
    }
  }

  const handleItineraryPhotoUpload = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    if (!userLoggedIn?.userToken) {
      setUploadError('Authentication token is missing. Please log in again.')
      return
    }

    const allowedTypes = ['image/jpeg', 'image/png']
    const maxFileSize = 20 * 1024 * 1024

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`Invalid file type for ${file.name}. Please upload JPEG or PNG images only.`)
        return
      }
      if (file.size > maxFileSize) {
        setUploadError(`File ${file.name} exceeds the 20MB limit.`)
        return
      }
    }

    setCurrentUploadingItinerary(index)
    setUploading(true)
    setUploadError(null)

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData()
        formDataUpload.append('fileName', file)
        formDataUpload.append('deviceType', 'WEBSITE')
        formDataUpload.append('project', 'cms')

        const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}/upload`, {
          method: 'POST',
          headers: {
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: formDataUpload,
        })

        if (!response.ok) {
          throw new Error(`Upload failed for ${file.name}`)
        }

        const result = await response.json()
        if (result.result && result.fileUrl) {
          return result.fileUrl
        } else {
          throw new Error(result.message || `Upload failed for ${file.name}`)
        }
      })

      const uploadedUrls = await Promise.all(uploadPromises)

      const currentItineraries = formData.itineraries || []
      const newItineraries = currentItineraries.map((item, i) => {
        if (i === index) {
          const currentImages = item.images || []
          return {
            ...item,
            hasPhoto: true,
            images: [...currentImages, ...uploadedUrls],
          }
        }
        return item
      })

      updateFormData({ itineraries: newItineraries })
    } catch (err: any) {
      setUploadError(err.message || 'An error occurred during upload')
    } finally {
      setUploading(false)
      setCurrentUploadingItinerary(null)
    }
  }

  const handleItineraryPhotoClick = (index: number) => {
    if (itineraryFileInputRefs.current[index]) {
      itineraryFileInputRefs.current[index]?.click()
    }
  }

  const handleRemoveItineraryPhoto = (itineraryIndex: number, imageIndex: number) => {
    const currentItineraries = formData.itineraries || []
    const newItineraries = currentItineraries.map((item, i) => {
      if (i === itineraryIndex) {
        const newImages = (item.images || []).filter((_, imgIndex) => imgIndex !== imageIndex)
        return {
          ...item,
          images: newImages,
          hasPhoto: newImages.length > 0,
        }
      }
      return item
    })
    updateFormData({ itineraries: newItineraries })
  }

  const handleSchedulePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    if (!userLoggedIn?.userToken) {
      setUploadError('Authentication token is missing. Please log in again.')
      return
    }

    const allowedTypes = ['image/jpeg', 'image/png']
    const maxFileSize = 20 * 1024 * 1024

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`Invalid file type for ${file.name}. Please upload JPEG or PNG images only.`)
        return
      }
      if (file.size > maxFileSize) {
        setUploadError(`File ${file.name} exceeds the 20MB limit.`)
        return
      }
    }

    setScheduleUploading(true)
    setUploadError(null)

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData()
        formDataUpload.append('fileName', file)
        formDataUpload.append('deviceType', 'WEBSITE')
        formDataUpload.append('project', 'cms')

        const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}/upload`, {
          method: 'POST',
          headers: {
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: formDataUpload,
        })

        if (!response.ok) {
          throw new Error(`Upload failed for ${file.name}`)
        }

        const result = await response.json()
        if (result.result && result.fileUrl) {
          return result.fileUrl
        } else {
          throw new Error(result.message || `Upload failed for ${file.name}`)
        }
      })

      // Get the URLs of all successfully uploaded images
      const uploadedUrls = await Promise.all(uploadPromises)

      // Get the current images and add the new ones to the array
      const currentImages = formData.scheduleImages || []
      const newImages = [...currentImages, ...uploadedUrls]

      // Update the formData state with the new images and hasPhoto flag
      updateFormData({
        scheduleImages: newImages,
        scheduleHasPhoto: newImages.length > 0,
      })
    } catch (err: any) {
      setUploadError(err.message || 'An error occurred during upload')
    } finally {
      setScheduleUploading(false)
    }
  }

  const handleSchedulePhotoClick = () => {
    if (scheduleFileInputRef.current) {
      scheduleFileInputRef.current.click()
    }
  }

  const handleRemoveSchedulePhoto = (imageIndex: number) => {
    const currentImages = formData.scheduleImages || []
    const newImages = currentImages.filter((_, index) => index !== imageIndex)
    updateFormData({
      scheduleImages: newImages,
      scheduleHasPhoto: newImages.length > 0,
    })
  }

  const isPricingComplete =
    formData.pricePerPerson?.trim() !== '' &&
    (formData.freeForChild || formData.pricePerChild?.trim() !== '')

  const canProceed =
    formData.packageName?.trim() !== '' &&
    formData.whatsIncluded?.some((item) => item.trim()) &&
    formData.whatsNotIncluded?.some((item) => item.trim()) &&
    formData.importantInfo?.some((item) => item.trim()) &&
    isPricingComplete

  const renderDynamicSection = (
    title: string,
    listName: string,
    items:
      | string[]
      | {
          title: string
          subtitle: string
          lat: number | null
          long: number | null
          details: string
        }[],
    isRequired = false,
  ) => {
    const isMeetingPoints = listName === 'meetingPoints'
    const safeItems = Array.isArray(items)
      ? items
      : isMeetingPoints
      ? [{ title: '', subtitle: '', lat: null, long: null, details: '' }]
      : ['']

    return (
      <div style={styles.section}>
        <label style={{ ...styles.label, marginBottom: '8px' }}>
          {title}
          {isRequired && <span style={{ color: '#ef4444' }}> *</span>}
        </label>
        <div>
          {safeItems.map((item: any, index) => (
            <div
              key={index}
              style={{ marginBottom: index === safeItems.length - 1 ? '0' : '12px' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {isMeetingPoints ? (
                  <div style={{ width: '100%' }}>
                    {isLoaded && window.google?.maps ? (
                      <>
                        <StandaloneSearchBox
                          onLoad={(ref) => {
                            searchBoxRef.current = ref
                            if (window.google?.maps) {
                              const bounds = new window.google.maps.LatLngBounds(
                                { lat: 5.6, lng: 97.3 },
                                { lat: 45.6, lng: 146.0 },
                              )
                              ref.setBounds(bounds)
                            }
                          }}
                          onPlacesChanged={() => {
                            const places = searchBoxRef.current?.getPlaces()
                            const place = places?.[0]
                            if (place?.geometry?.location) {
                              handleMeetingPointChange(index, {
                                title: place.name || '',
                                subtitle: place.formatted_address || '',
                                lat: place.geometry.location.lat(),
                                long: place.geometry.location.lng(),
                                details: item.details,
                              })
                            }
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Search location or click/drag pin on map"
                            style={{
                              ...styles.input,
                              marginBottom: '8px',
                            }}
                          />
                        </StandaloneSearchBox>
                        <div
                          style={{
                            position: 'relative',
                            height: '300px',
                            width: '100%',
                            marginBottom: '12px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={{ lat: item.lat || 13.72, lng: item.long || 100.52 }}
                            zoom={item.lat && item.long ? 15 : 12}
                            options={{
                              fullscreenControl: false,
                              zoomControl: true,
                              streetViewControl: false,
                              mapTypeControl: false,
                            }}
                            onLoad={(map) => {
                              mapRefs.current[index] = map
                            }}
                            onClick={(e) => {
                              const lat = e.latLng?.lat()
                              const lng = e.latLng?.lng()
                              if (lat && lng) {
                                reverseGeocode(lat, lng, index)
                              }
                            }}
                          >
                            <Marker
                              position={{ lat: item.lat || 13.72, lng: item.long || 100.52 }}
                              draggable={true}
                              onDragEnd={(e) => {
                                const lat = e.latLng?.lat()
                                const lng = e.latLng?.lng()
                                if (lat && lng) {
                                  reverseGeocode(lat, lng, index)
                                }
                              }}
                            />
                          </GoogleMap>
                        </div>
                      </>
                    ) : (
                      <div>Loading Map...</div>
                    )}

                    {/* Only show name and address if a location is selected */}
                    {(item.title || item.subtitle) && (
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ ...styles.label, marginBottom: '0' }}>
                          {item.title || 'Meeting Point Name'}
                        </label>
                        <p style={{ margin: '0', fontSize: '14px', color: '#667085' }}>
                          {item.subtitle || 'Meeting Point Address'}
                        </p>
                      </div>
                    )}

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ ...styles.label, marginBottom: '4px' }}>
                        Meeting Point Details
                      </label>
                      <input
                        type="text"
                        placeholder="Enter meeting point details"
                        value={item.details || ''}
                        onChange={(e) =>
                          handleMeetingPointChange(index, { details: e.target.value })
                        }
                        style={styles.input}
                      />
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={item || ''}
                    onChange={(e) => handleListItemChange(listName, index, e.target.value)}
                    maxLength={200}
                    style={{
                      ...styles.input,
                      flex: 1,
                    }}
                  />
                )}
                {index === 0 && !isMeetingPoints ? (
                  <button
                    onClick={() => (isMeetingPoints ? addMeetingPoint() : addListItem(listName))}
                    style={{
                      ...styles.actionButton,
                      background: '#F0F9FF',
                      borderColor: '#0048B3',
                      color: '#0048B3',
                    }}
                  >
                    +
                  </button>
                ) : index > 0 ? (
                  <button
                    onClick={() =>
                      isMeetingPoints ? removeMeetingPoint(index) : removeListItem(listName, index)
                    }
                    style={{
                      ...styles.actionButton,
                      background: '#FEF2F2',
                      borderColor: '#FECACA',
                      color: '#EF4444',
                    }}
                  >
                    <DeleteOutlined />
                  </button>
                ) : null}
              </div>
              {isMeetingPoints ? (
                <div style={styles.charCount}>
                  {item.lat && <span style={{ color: '#10B981' }}>Location selected</span>}
                </div>
              ) : (
                <div style={styles.charCount}>{(item || '').length}/200 characters</div>
              )}
            </div>
          ))}
          {!isMeetingPoints && (
            <button
              onClick={() => addListItem(listName)}
              style={{
                backgroundColor: 'transparent',
                color: '#0048B3',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                padding: '0',
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>
              Add another item
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderItinerarySection = () => {
    const currentItineraries = formData.itineraries || [
      { title: '', duration: '', details: '', hasPhoto: false, images: [] },
    ]

    return (
      <div style={styles.section}>
        <div
          onClick={() => updateFormData({ accordionOpen: !formData.accordionOpen })}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            border: '1px solid #D6DAE0',
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: formData.accordionOpen ? '#F9FAFB' : '#FFFFFF',
            transition: 'all 0.2s ease',
          }}
        >
          <label style={{ ...styles.label, margin: '0' }}>
            Itineraries <span style={{ color: '#667085' }}>(optional)</span>
          </label>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: formData.accordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="#667085"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {formData.accordionOpen && (
          <div style={{ marginTop: '16px' }}>
            {currentItineraries.map((itinerary, index) => (
              <div key={index} style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <h4
                    style={{
                      margin: '0',
                      fontSize: '14px',
                      color: '#212427',
                      fontWeight: '600',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Itinerary {index + 1}
                  </h4>

                  {index > 0 && (
                    <button
                      onClick={() => removeItinerary(index)}
                      style={{
                        background: '#FEF2F2',
                        border: '1px solid #FECACA',
                        color: '#EF4444',
                        fontSize: '12px',
                        cursor: 'pointer',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: '500',
                        height: '32px',
                      }}
                    >
                      Delete itinerary
                    </button>
                  )}
                </div>

                <div style={styles.subSection}>
                  <label style={styles.subLabel}>Itinerary title</label>
                  <input
                    type="text"
                    placeholder="Enter itinerary title"
                    value={itinerary.title}
                    onChange={(e) => handleItineraryChange(index, 'title', e.target.value)}
                    style={styles.input}
                  />
                </div>

                <div style={styles.subSection}>
                  <label style={styles.subLabel}>Duration</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Duration of the itinerary"
                      value={itinerary.duration}
                      onChange={(e) => handleItineraryChange(index, 'duration', e.target.value)}
                      style={{
                        ...styles.input,
                        paddingRight: '50px',
                      }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '16px',
                        color: '#667085',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      min
                    </span>
                  </div>
                </div>

                <div style={styles.subSection}>
                  <label style={styles.subLabel}>Itinerary details</label>
                  <textarea
                    placeholder="Enter itinerary details"
                    value={itinerary.details}
                    onChange={(e) => handleItineraryChange(index, 'details', e.target.value)}
                    rows={3}
                    style={styles.textarea}
                  />
                </div>

                <div style={styles.subSection}>
                  <label style={styles.subLabel}>
                    Itinerary photos <span style={{ color: '#667085' }}>(optional)</span>
                  </label>

                  <input
                    type="file"
                    ref={(el) => (itineraryFileInputRefs.current[index] = el)}
                    onChange={(e) => handleItineraryPhotoUpload(index, e)}
                    accept=".jpg,.jpeg,.png"
                    multiple
                    style={{ display: 'none' }}
                  />

                  {(itinerary.images || []).length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                          gap: '8px',
                          marginBottom: '12px',
                        }}
                      >
                        {(itinerary.images || []).map((imageUrl, imageIndex) => (
                          <div
                            key={imageIndex}
                            style={{
                              position: 'relative',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              aspectRatio: '1',
                            }}
                          >
                            <img
                              src={imageUrl}
                              alt={`Itinerary ${index + 1} - ${imageIndex + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            <button
                              onClick={() => handleRemoveItineraryPhoto(index, imageIndex)}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                background: 'rgba(0, 0, 0, 0.6)',
                                border: 'none',
                                borderRadius: '50%',
                                color: 'white',
                                cursor: 'pointer',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                              }}
                              disabled={uploading && currentUploadingItinerary === index}
                            >
                              <CloseOutlined />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    onClick={() => handleItineraryPhotoClick(index)}
                    style={{
                      border: itinerary.hasPhoto ? '2px solid #10B981' : '2px dashed #D6DAE0',
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      backgroundColor: itinerary.hasPhoto ? '#F0FDF4' : '#F9FAFB',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                      {uploading && currentUploadingItinerary === index
                        ? '⏳'
                        : itinerary.hasPhoto
                        ? '✅'
                        : '📷'}
                    </div>
                    <p
                      style={{
                        color: itinerary.hasPhoto ? '#10B981' : '#0048B3',
                        fontWeight: '500',
                        fontSize: '14px',
                        fontFamily: 'Poppins, sans-serif',
                        margin: '0',
                      }}
                    >
                      {uploading && currentUploadingItinerary === index
                        ? 'Uploading...'
                        : itinerary.hasPhoto
                        ? `${(itinerary.images || []).length} photo${
                            (itinerary.images || []).length > 1 ? 's' : ''
                          } uploaded`
                        : 'Upload or take photos'}
                    </p>
                    {uploadError && currentUploadingItinerary === index && (
                      <p
                        style={{
                          color: '#ef4444',
                          fontSize: '12px',
                          marginTop: '4px',
                          margin: '4px 0 0 0',
                        }}
                      >
                        {uploadError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addItinerary}
              style={{
                backgroundColor: 'transparent',
                color: '#0048B3',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>
              Add new itinerary
            </button>
          </div>
        )}
      </div>
    )
  }

  const renderScheduleSection = () => {
    const currentScheduleImages = formData.scheduleImages || []
    return (
      <div style={styles.section}>
        <div
          onClick={() => updateFormData({ scheduleAccordionOpen: !formData.scheduleAccordionOpen })}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            border: '1px solid #D6DAE0',
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: formData.scheduleAccordionOpen ? '#F9FAFB' : '#FFFFFF',
            transition: 'all 0.2s ease',
          }}
        >
          <label style={{ ...styles.label, margin: '0' }}>
            Schedule <span style={{ color: '#667085' }}>(optional)</span>
          </label>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: formData.scheduleAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="#667085"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {formData.scheduleAccordionOpen && (
          <div style={{ marginTop: '16px' }}>
            <div style={styles.subSection}>
              <textarea
                placeholder="Write a short schedule"
                value={formData.schedule || ''}
                onChange={(e) => handleInputChange('schedule', e.target.value)}
                maxLength={500}
                rows={4}
                style={styles.textarea}
              />
              <div style={styles.charCount}>{(formData.schedule || '').length}/500 characters</div>
            </div>

            <div style={styles.subSection}>
              <label style={styles.subLabel}>
                Schedule photos <span style={{ color: '#667085' }}>(optional)</span>
              </label>

              <input
                type="file"
                ref={scheduleFileInputRef}
                onChange={handleSchedulePhotoUpload}
                accept=".jpg,.jpeg,.png"
                multiple
                style={{ display: 'none' }}
              />

              {currentScheduleImages.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                      gap: '8px',
                      marginBottom: '12px',
                    }}
                  >
                    {currentScheduleImages.map((imageUrl, imageIndex) => (
                      <div
                        key={imageIndex}
                        style={{
                          position: 'relative',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          aspectRatio: '1',
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`Schedule ${imageIndex + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <button
                          onClick={() => handleRemoveSchedulePhoto(imageIndex)}
                          style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            border: 'none',
                            borderRadius: '50%',
                            color: 'white',
                            cursor: 'pointer',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                          }}
                          disabled={scheduleUploading}
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div
                onClick={handleSchedulePhotoClick}
                style={{
                  border: formData.scheduleHasPhoto ? '2px solid #10B981' : '2px dashed #D6DAE0',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  backgroundColor: formData.scheduleHasPhoto ? '#F0FDF4' : '#F9FAFB',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                  {scheduleUploading ? '⏳' : formData.scheduleHasPhoto ? '✅' : '🚌'}
                </div>
                <p
                  style={{
                    color: formData.scheduleHasPhoto ? '#10B981' : '#0048B3',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                    margin: '0',
                  }}
                >
                  {scheduleUploading
                    ? 'Uploading...'
                    : formData.scheduleHasPhoto
                    ? `${currentScheduleImages.length} photo${
                        currentScheduleImages.length > 1 ? 's' : ''
                      } uploaded`
                    : 'Upload or take photos'}
                </p>
                {uploadError && scheduleUploading && (
                  <p
                    style={{
                      color: '#ef4444',
                      fontSize: '12px',
                      marginTop: '4px',
                      margin: '4px 0 0 0',
                    }}
                  >
                    {uploadError}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        boxSizing: 'border-box',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            fontSize: '14px',
            color: '#667085',
            marginBottom: '8px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          STEP 4 OF 5
        </div>
        <h2
          style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#212427',
            margin: '0',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Add package details
        </h2>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>
          Package name <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="text"
          value={formData.packageName || ''}
          onChange={(e) => handleInputChange('packageName', e.target.value)}
          maxLength={100}
          style={styles.input}
        />
        <div style={styles.charCount}>{(formData.packageName || '').length}/100 characters</div>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>
          Short description <span style={{ color: '#667085' }}>(optional)</span>
        </label>
        <input
          type="text"
          value={formData.shortDescription || ''}
          onChange={(e) => handleInputChange('shortDescription', e.target.value)}
          maxLength={200}
          style={styles.input}
        />
        <div style={styles.charCount}>
          {(formData.shortDescription || '').length}/200 characters
        </div>
      </div>

      {renderDynamicSection(
        "What's included",
        'whatsIncluded',
        formData.whatsIncluded || [''],
        true,
      )}
      {renderDynamicSection(
        "What's not included",
        'whatsNotIncluded',
        formData.whatsNotIncluded || [''],
        true,
      )}
      {renderDynamicSection(
        'Important information',
        'importantInfo',
        formData.importantInfo || [''],
        true,
      )}

      <div style={{ ...styles.section, marginTop: '24px' }}>
        <h3 style={{ ...styles.label, fontWeight: '600', fontSize: '18px', margin: '0' }}>
          Pricing
        </h3>
        <div style={{ marginTop: '16px' }}>
          <div style={styles.subSection}>
            <label style={styles.subLabel}>
              Price per person <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={styles.inputWithDropdown}>
              <input
                type="number"
                value={formData.pricePerPerson || ''}
                onChange={(e) => handleInputChange('pricePerPerson', e.target.value)}
                style={styles.flexInput}
              />
              <div style={styles.dropdown}>
                {formData.currency || 'THB'}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginLeft: '8px' }}
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="#667085"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div style={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="freeForChildCheckbox"
              checked={formData.freeForChild || false}
              onChange={handleCheckboxChange}
              style={styles.checkbox}
            />
            <label htmlFor="freeForChildCheckbox" style={{ ...styles.label, marginBottom: '0' }}>
              Free for child
            </label>
          </div>

          {!formData.freeForChild && (
            <div style={styles.subSection}>
              <label style={styles.subLabel}>
                Price per child <span style={{ color: '#667085' }}>(age : 0-12)</span>
                <span style={{ color: '#ef4444' }}> *</span>
              </label>
              <div style={styles.inputWithDropdown}>
                <input
                  type="number"
                  value={formData.pricePerChild || ''}
                  onChange={(e) => handleInputChange('pricePerChild', e.target.value)}
                  style={styles.flexInput}
                />
                <div style={styles.dropdown}>
                  {formData.currency || 'THB'}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginLeft: '8px' }}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="#667085"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          )}

          <div style={styles.subSection}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ ...styles.subLabel, marginBottom: '0' }}>
                Extra expenses travelers should prepare{' '}
                <span style={{ color: '#667085' }}>(optional)</span>
              </label>
            </div>
            <textarea
              placeholder="Write extra expenses"
              value={formData.extraExpenses || ''}
              onChange={(e) => handleInputChange('extraExpenses', e.target.value)}
              maxLength={500}
              rows={3}
              style={styles.textarea}
            />
            <div style={styles.charCount}>
              {(formData.extraExpenses || '').length}/500 characters
            </div>
          </div>

          <div style={styles.subSection}>
            <label style={styles.subLabel}>
              Add discount <span style={{ color: '#667085' }}>(Optional)</span>
            </label>
            <div style={styles.inputWithDropdown}>
              <input
                type="text"
                value={formData.discount || ''}
                onChange={(e) => handleInputChange('discount', e.target.value)}
                style={{ ...styles.flexInput, border: 'none', paddingRight: '16px' }}
              />
            </div>
          </div>
        </div>
      </div>

      {renderDynamicSection('Meeting points', 'meetingPoints', formData.meetingPoints)}
      {renderItinerarySection()}
      {renderScheduleSection()}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '32px',
        }}
      >
        <button
          onClick={onPrevious}
          style={{
            padding: '12px 24px',
            backgroundColor: 'transparent',
            color: '#667085',
            border: '1px solid #D6DAE0',
            borderRadius: '24px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'Poppins, sans-serif',
            height: '48px',
          }}
        >
          ← Back
        </button>

        <button
          onClick={onNext}
          disabled={!canProceed}
          style={{
            padding: '12px 24px',
            backgroundColor: canProceed ? '#0048B3' : '#D6DAE0',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            cursor: canProceed ? 'pointer' : 'not-allowed',
            fontWeight: '500',
            fontSize: '16px',
            fontFamily: 'Poppins, sans-serif',
            height: '48px',
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default Step4
